import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Stack,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { Lead } from '../../types/lead.types';
import { StatusChip } from './StatusChip';
import { PriorityBadge } from './PriorityBadge';
import { useAuthStore } from '../../store/authStore';
import LeadAssignmentModal from './LeadAssignmentModal';

interface LeadCardProps {
  lead: Lead;
  onEdit: (lead: Lead) => void;
  onView: (lead: Lead) => void;
  onAssignmentChange?: () => void;
}

export const LeadCard: React.FC<LeadCardProps> = ({ lead, onEdit, onView, onAssignmentChange }) => {
  const { profile } = useAuthStore();
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  
  // Only org_admin can edit/delete/assign leads
  const canEdit = profile?.org_role === 'org_admin';
  const canDelete = profile?.org_role === 'org_admin';
  const canAssign = profile?.org_role === 'org_admin';
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleAssignmentComplete = () => {
    // Refresh the lead data if callback provided
    if (onAssignmentChange) {
      onAssignmentChange();
    }
  };

  return (
    <>
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" component="div">
            {lead.first_name} {lead.last_name}
          </Typography>
          <PriorityBadge priority={lead.priority} />
        </Box>

        <StatusChip status={lead.status} size="small" />

        <Stack spacing={1} sx={{ mt: 2 }}>
          {lead.company && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {lead.company}
              </Typography>
            </Box>
          )}

          {lead.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary" noWrap>
                {lead.email}
              </Typography>
            </Box>
          )}

          {lead.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {lead.phone}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Assignment Status */}
        {lead.assigned_to && (
          <Box sx={{ mt: 2 }}>
            <Chip
              label={`Assigned`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        )}

        {lead.estimated_value && (
          <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
            ${lead.estimated_value.toLocaleString()}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Created: {formatDate(lead.created_at)}
        </Typography>
      </CardContent>

      <CardActions>
        <Button size="small" onClick={() => onView(lead)}>
          View Details
        </Button>
        {canEdit && (
          <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(lead)}>
            Edit
          </Button>
        )}
        {canAssign && (
          <Button 
            size="small" 
            startIcon={<PersonAddIcon />} 
            onClick={() => setAssignModalOpen(true)}
            color="primary"
          >
            Assign
          </Button>
        )}
      </CardActions>
    </Card>

    {/* Assignment Modal */}
    <LeadAssignmentModal
      open={assignModalOpen}
      onClose={() => setAssignModalOpen(false)}
      lead={lead}
      onAssignmentComplete={handleAssignmentComplete}
    />
    </>
  );
};